import React, { useEffect, useState } from 'react';
import { compose } from 'ramda';
import Dashboard from 'components/Layout/Dashboard/Dashboard';
import withMeta from 'hoc/withMeta';
import { ContributionType } from 'store/modules/ExistingPolicies/types';
import { WithdrawalType } from 'store/modules/Withdrawals/types';
import Store from 'store/store';
import Router from 'components/Router/Router';
import { Loader, Button } from 'components/Base';
import { ExistingPolicyType } from 'store/modules/ExistingPolicies/types';
import { PolicyType } from 'store/modules/Policies/types';
import Recommendations from 'components/Recommendations/Recommendations';
import ContributionsSummary from 'components/Recommendations/ContributionsSummary';
import withCaseInfo from 'hoc/withCaseInfo';
import { humanizePolicyType, Notify } from 'utils';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import api from 'api';
import {
  fetchDataIfNeeded,
  getExistingPolicies,
  getPolicies,
  getRiskProfiles
} from 'components/Cases/utils';
import { useDispatch } from 'react-redux';

const { connect, actions, selectors } = Store;
const { useHistory, useParams } = Router;

interface ContributionsProps {
  contributions: Array<ContributionType>;
  withdrawals: Array<WithdrawalType>;
  existingPolicies: Array<ExistingPolicyType>;
  recommendedPolicies: Array<PolicyType>;
  getRecommendationsData: any;
  getPastPerformanceData: any;
  riskSummaries: any;
  updatePerformancePeriod: Function;
  setRiskSummaries: any;
  setContributionsSummary: Function;
}

const RecommendationsPage = ({
  contributions,
  withdrawals,
  existingPolicies,
  recommendedPolicies,
  getPastPerformanceData,
  riskSummaries,
  updatePerformancePeriod,
  setContributionsSummary
}: ContributionsProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPastPerformanceData, setIsLoadingPastPerformanceData] =
    useState(true);
  const { portfolio } = useParams() as any;

  const getAllData = () => {
    Promise.all([
      dispatch(getExistingPolicies(portfolio) as any),
      dispatch(getPolicies(portfolio) as any),
      dispatch(
        fetchDataIfNeeded('contributionsSummary', () =>
          api.getContributionsSummary({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('contributions', () =>
          api.getContributions({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('withdrawals', () =>
          api.getWithdrawals({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('assetAllocations', () =>
          api.getAssetAllocationData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('portfolioRiskScore', () =>
          api.getPortfolioRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesCharges', () =>
          api.getExsitinPoliciesChargesData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedPoliciesCharges', () =>
          api.getRecommendedPoliciesChargesData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesRiskScore', () =>
          api.getExistingPoliciesRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedPoliciesRiskScore', () =>
          api.getRecommendedPoliciesRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('sectorBreakdown', () =>
          api.getSectorBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('regionlBreakdown', () =>
          api.getRegionalBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('countryBreakdown', () =>
          api.getCountryBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(fetchDataIfNeeded('providers', () => api.getProviders()) as any),
      dispatch(
        fetchDataIfNeeded('holdings', () =>
          api.getHoldings({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedRiskAnalysis', () =>
          api.getRiskAnalysisData({ portfolio })
        ) as any
      )
    ])
      // eslint-disable-next-line
      .then(([existingPolicies, policies, contributionsSummary, ...data]) => {
        dispatch(setContributionsSummary(contributionsSummary.data));
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(getRiskProfiles(portfolio) as any);
    updatePerformancePeriod();
    getAllData();
    getPastPerformanceData({ portfolio })
      .then(() => {
        setIsLoadingPastPerformanceData(false);
      })
      .catch(() => {
        setIsLoadingPastPerformanceData(false);
        Notify.error('Error while loading past performance data');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cutPid = (pid?: string) => {
    if (!pid) return;
    const pidSplited = pid.split('-');

    return pidSplited[0];
  };

  const initialValues = {
    policies: existingPolicies
      .filter((policy: ExistingPolicyType) => policy.id)
      .map((policy: ExistingPolicyType) => {
        return {
          ...policy,
          owner: policy.owner.display_name,
          isProcessed: policy.is_processed,
          total_ongoing_charge: Number(policy.total_ongoing_charge) * 100
        };
      }),
    contributions: contributions.map((contributions: any) => {
      const policyType = humanizePolicyType(
        contributions.destination_policy.type
      );
      return {
        id: contributions.id,
        owner: contributions.owner.id,
        amount: contributions.amount,
        source: contributions.source,
        frequency: contributions.frequency,
        source_account: contributions.source_account,
        destination_account: contributions.destination_account,
        destination_policy: `${policyType} - ${cutPid(
          contributions.destination_policy.pid
        )}`
      };
    }),
    withdrawals: withdrawals.map((withdrawal: any) => {
      const policyType = humanizePolicyType(withdrawal.source_policy.type);
      return {
        id: withdrawal.id,
        owner: withdrawal.owner.id,
        amount: withdrawal.amount,
        source: withdrawal.source,
        frequency: withdrawal.frequency,
        source_account: withdrawal.source_account,
        destination_account: withdrawal.destination_account,
        source_policy: `${policyType} - ${cutPid(withdrawal.source_policy.pid)}`
      };
    }),
    recommendedPolicies: recommendedPolicies.map((policy: PolicyType) => {
      return {
        ...policy,
        owner: policy.owner.id,
        total_ongoing_charge: Number(policy.total_ongoing_charge) * 100,
        pid: cutPid(policy.pid),
        haveFunds: policy.fund_allocations.length > 0,
        annualised_contribution: policy.annualised_contribution || 0,
        annualised_withdrawal: policy.annualised_withdrawal || 0
      };
    })
  };

  return (
    <Dashboard>
      <Loader isLoading={isLoading}>
        {riskSummaries.length === 0 ||
        !riskSummaries[0].time_horizon ||
        !riskSummaries[0].recommended_risk_profile ? (
          <>
            <>
              {' '}
              <div
                style={{
                  textAlign: 'center',
                  marginBottom: '1rem',
                  padding: '2rem'
                }}
              >
                <CreateNewFolderIcon
                  htmlColor="#6b7280"
                  style={{ width: '36px', height: '36px' }}
                />
                <div style={{ fontWeight: 600 }}>No Risk Analysis</div>
                <div style={{ color: '#6b7280' }}>
                  Please complete risk analysis.
                </div>
                <div
                  style={{
                    padding: '1rem 0rem 0rem'
                  }}
                >
                  <Button
                    onClick={() => history.push(`/${portfolio}/risk-summary`)}
                  >
                    View Risk Analysis
                  </Button>
                </div>
              </div>
            </>
          </>
        ) : (
          <>
            <ContributionsSummary />
            <Recommendations
              initialValues={initialValues}
              isLoadingPastPerformanceData={isLoadingPastPerformanceData}
            />
          </>
        )}
      </Loader>
    </Dashboard>
  );
};

export default compose(
  connect(
    state => ({
      contributions: selectors.caseSelectors.contributions(state),
      withdrawals: selectors.caseSelectors.withdrawals(state),
      recommendedPolicies: selectors.caseSelectors.policies(state),
      existingPolicies: selectors.caseSelectors.existingPolicies(state),
      riskSummaries: selectors.caseSelectors.getRiskSummary(state)
    }),
    {
      getRecommendationsData:
        actions.recommendationActions.getRecommendationData,
      getPastPerformanceData:
        actions.recommendationActions.getPastPerformanceData,
      updatePerformancePeriod:
        actions.investmentAnalyserActions.updatePerformancePeriod,
      setRiskSummaries: actions.caseActions.setRiskSummaries,
      setContributionsSummary: actions.caseActions.setContributionsSummary
    }
  ),
  withMeta,
  withCaseInfo
)(RecommendationsPage);
